import React, { useState, useEffect } from "react";

import { API, graphqlOperation, Storage } from "aws-amplify";
import { inboundShipmentByReloadYardByAccount } from "../../../graphql/inbound/custom-queries";
import { invoiceByAccountCount } from "../../../graphql/invoice/invoice-queries";
import { outboundShipmentByAccountByStatusByDates } from "../../../graphql/queries";
import {
  deleteChargeRule,
  deleteProduct,
  deleteProductStats,
  deleteSavedAddress,
  deleteService,
} from "../../../graphql/mutations";

import { makeStyles, Tooltip } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import {
  useDataProvider,
  useNotify,
  useRedirect,
  Button,
  Confirm,
} from "react-admin";

import { DeleteIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import { removeFile } from "../../../utils/fileUtils";
import { hasExternalId } from "../../../utils/zapierUtils";

import { fetchProducts, fetchSavedAddresses } from "./AccountUtils";
import { fetchServices } from "../services/ServiceUtils";
import { fetchChargeRules } from "./chargeRules/ChargeRuleUtils";
import { isMissingProductStatsError } from "../products/ProductUtils";

const useStyles = makeStyles(
  (theme) => ({
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

// If account has no dependencies and no externalId, then delete the account.
// Otherwise, disable DELETE and only allow Archive/Restore.
export const DeepDeleteButton = ({
  record,
  resource,
  isDeleting,
  setIsDeleting,
  isSaving,
  className,
  ...props
}) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [hasDependencies, setHasDependencies] = useState(true);
  const wasSynced = hasExternalId(record);
  const toolTipMessage = wasSynced
    ? "Account sync'ed to an external system."
    : "Account has dependent records.";

  const handleDialogOpen = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  useEffect(() => {
    if (record.reloadYardId) getDependencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const getDependencies = async () => {
    try {
      debugger;
      const promiseList = [];

      promiseList.push(
        API.graphql(
          graphqlOperation(inboundShipmentByReloadYardByAccount, {
            reloadYardId: record.reloadYardId,
            accountId: { eq: record.id },
            limit: 1,
          }),
        ),
      );

      promiseList.push(
        API.graphql(
          graphqlOperation(outboundShipmentByAccountByStatusByDates, {
            accountId: record.id,
            limit: 1,
          }),
        ),
      );

      promiseList.push(
        API.graphql(
          graphqlOperation(invoiceByAccountCount, {
            accountId: record.id,
            limit: 1,
          }),
        ),
      );

      const [inboundResp, outboundResp, invoiceResp] =
        await Promise.all(promiseList);

      const inboundCount =
        inboundResp.data.inboundShipmentByReloadYardByAccount.items.length;
      const outboundCount =
        outboundResp.data.outboundShipmentByAccountByStatusByDates.items.length;
      const invoiceCount =
        invoiceResp.data.invoiceByAccountByActiveByStatusByDate.items.length;

      setHasDependencies(
        inboundCount > 0 || outboundCount > 0 || invoiceCount > 0,
      );
    } catch (err) {
      logError("accounts:DeepDeleteButton:getDependencies", err, false);
    }
  };

  const handleDelete = async (event) => {
    setOpen(false);
    setIsDeleting(true);

    try {
      if (record.attachments?.length > 0) {
        await Promise.all(
          record.attachments.map((itm) => {
            return Storage.remove(itm.key);
          }),
        );
      }

      if (record.bolTemplate) {
        await removeFile(record, null, "bolTemplate");
      }

      if (record.receiptTemplate) {
        await removeFile(record, null, "receiptTemplate");
      }

      // (accountId, event, activeOnly) — sweep inactive (soft-deleted) rules too
      const rules = await fetchChargeRules(record.id, null, false);
      if (rules?.length > 0) {
        const results = await Promise.allSettled(
          rules.map((rule) => {
            return API.graphql(
              graphqlOperation(deleteChargeRule, {
                input: { id: rule.id },
              }),
            );
          }),
        );

        const resError = results.find((itm) => itm.status === "rejected");
        if (resError) {
          logError(
            "accounts:DeepDeleteButton:handleDelete:deleteChargeRule",
            resError,
            false,
          );
          throw resError;
        }
      }

      const services = await fetchServices(record.id, false);
      if (services?.length > 0) {
        const results = await Promise.allSettled(
          services.map((item) => {
            return API.graphql(
              graphqlOperation(deleteService, {
                input: { id: item.id },
              }),
            );
          }),
        );

        const resError = results.find((itm) => itm.status === "rejected");
        if (resError) {
          logError(
            "accounts:DeepDeleteButton:handleDelete:deleteService",
            resError,
            false,
          );
          throw resError;
        }
      }

      const products = await fetchProducts(record.id, false);
      if (products?.length > 0) {
        // ProductStats.id === Product.id, so delete the companion ProductStats row
        // before deleting the Product. If a ProductStats row does not exist, ignore
        // that specific not-found / conditional-delete error and continue.
        const statsResults = await Promise.allSettled(
          products.map((item) => {
            return API.graphql(
              graphqlOperation(deleteProductStats, {
                input: { id: item.id },
              }),
            );
          }),
        );

        const statsDeleteError = statsResults.find((result) => {
          if (result.status !== "rejected") return false;

          // Ignore missing ProductStats rows. Some products may predate the new table.
          return !isMissingProductStatsError(result.reason);
        });

        if (statsDeleteError) {
          logError(
            "accounts:DeepDeleteButton:handleDelete:deleteProductStats",
            statsDeleteError,
            false,
          );

          throw statsDeleteError;
        }

        const prodResults = await Promise.allSettled(
          products.map((item) => {
            return API.graphql(
              graphqlOperation(deleteProduct, {
                input: { id: item.id },
              }),
            );
          }),
        );

        const resError = prodResults.find((itm) => itm.status === "rejected");

        if (resError) {
          logError(
            "accounts:DeepDeleteButton:handleDelete:deleteProduct",
            resError,
            false,
          );

          throw resError;
        }
      }

      const savedAddresses = await fetchSavedAddresses(record.id);
      if (savedAddresses?.length > 0) {
        const addrResults = await Promise.allSettled(
          savedAddresses.map((item) => {
            if (item.id) {
              return API.graphql(
                graphqlOperation(deleteSavedAddress, {
                  input: { id: item.id },
                }),
              );
            }

            return {};
          }),
        );

        const resError = addrResults.find((itm) => itm.status === "rejected");
        if (resError) {
          logError(
            "accounts:DeepDeleteButton:handleDelete:deleteSavedAddress",
            resError,
            false,
          );
          throw resError;
        }
      }

      await dataProvider.delete(resource, {
        id: record.id,
        previousData: record,
      });

      notify("Account deleted successfully", "success");
      redirect("/accounts");
    } catch (err) {
      logError(
        "accounts:DeepDeleteButton:handleDelete:deleteAccount",
        err,
        notify,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {wasSynced || hasDependencies ? (
        <Tooltip title={toolTipMessage}>
          <span>
            {/* <span> is required to wrap a disabled button so tooltip still works */}
            <Button
              label="Delete"
              disabled
              className={classnames(
                "ra-delete-button",
                classes.deleteButton,
                className,
              )}
              {...props}
            >
              <DeleteIcon />
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          label="Delete"
          disabled={isDeleting || isSaving}
          onClick={handleDialogOpen}
          className={classnames(
            "ra-delete-button",
            classes.deleteButton,
            className,
          )}
          {...props}
        >
          <DeleteIcon />
        </Button>
      )}

      <Confirm
        isOpen={open}
        loading={isDeleting}
        title={"Delete Confirmation"}
        content={"Are you sure you want to delete this Account?"}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};
